/**
 * Admin → Guidelines.
 *
 * Backend: `/admin/data/guidelines/*` (app/routers/admin_data.py); contract in
 * frontend_documentation/guidelines-and-dscr-pricing-admin.md.
 *
 * Storage is blob-backed per slug/version: `source.pdf` (the original import),
 * `guideline.md` (the editable source), `structured.json` (the codified rules).
 * EVERY save is a new immutable version with an edit log by user — so the four
 * write paths here (markdown save, structured save, codify re-run, revert) all
 * end with a version bump and a log refresh.
 *
 * Layout: a left rail of guidelines (list endpoint) + a detail pane with four
 * views — Document (markdown, view ⇄ edit), Rules (the structured JSON), Version
 * history (revert per row), and the codify agent's raw transcript.
 *
 * On the editor: the doc calls this "the TipTap editor", but the backend stores
 * and versions *markdown*. Round-tripping markdown through a rich-text model is
 * lossy (tables, the guideline's numbered rule blocks), so the edit mode is a
 * markdown source pane with a live rendered preview — what gets PUT is exactly
 * what the codify agent will read back.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AutoFixHighOutlined from "@mui/icons-material/AutoFixHighOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import RefreshRounded from "@mui/icons-material/Refresh";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import GuidelineMarkdown from "../../../components/markdown/GuidelineMarkdown";
import { adminGuidelinesApi } from "../../../api/adminDataApi";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { AdminCard, AdminEmpty, AdminScreenShell } from "../shared/AdminScreenShell";
import AdminEditLog from "../shared/AdminEditLog";
import { ExpandableBlock } from "../shared/adminGrid";
import PillSwitcher from "../shared/PillSwitcher";
import GuidelineRulesPanel from "./GuidelineRulesPanel";
import ImportGuidelinePdfDialog from "./ImportGuidelinePdfDialog";
import { teal } from "../../../theme/colors";

const VIEWS = [
  { key: "document", label: "Document" },
  { key: "rules", label: "Rules" },
  { key: "versions", label: "Version history" },
  { key: "transcript", label: "Agent transcript" },
];

const errText = (err, fallback) =>
  err?.response?.data?.detail || err?.message || fallback;

export default function AdminGuidelinesScreen() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { slug: slugParam } = useParams();

  const [list, setList] = useState(null);
  const [doc, setDoc] = useState(null);
  const [log, setLog] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("document");
  const [importOpen, setImportOpen] = useState(false);

  // Markdown edit state — draft is null when we're in read mode.
  const [draft, setDraft] = useState(null);
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(false);
  const [recodify, setRecodify] = useState(false);

  // Which version the detail pane is showing (null = current).
  const [viewVersion, setViewVersion] = useState(null);

  const fetchList = useCallback(() => {
    setLoadingList(true);
    adminGuidelinesApi
      .list()
      .then(setList)
      .catch((err) => {
        setList([]);
        enqueueSnackbar(errText(err, "Couldn't load guidelines"), {
          variant: "error",
        });
      })
      .finally(() => setLoadingList(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Land on the first guideline when none is selected.
  useEffect(() => {
    if (!slugParam && list?.length) {
      navigate(`/admin/guidelines/${list[0].slug}`, { replace: true });
    }
  }, [slugParam, list, navigate]);

  const loadDoc = useCallback(
    (slug, version) => {
      if (!slug) return;
      setLoadingDoc(true);
      setDraft(null);
      Promise.all([
        adminGuidelinesApi.get(slug, { version, includeRaw: true }),
        adminGuidelinesApi.log(slug).catch(() => []),
      ])
        .then(([d, l]) => {
          setDoc(d);
          setLog(l);
        })
        .catch((err) => {
          setDoc(null);
          enqueueSnackbar(errText(err, "Couldn't load guideline"), {
            variant: "error",
          });
        })
        .finally(() => setLoadingDoc(false));
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    setViewVersion(null);
    loadDoc(slugParam);
  }, [slugParam, loadDoc]);

  const isHistoricalView =
    viewVersion != null && viewVersion !== doc?.currentVersion;

  const handleSaveMarkdown = useCallback(() => {
    if (draft == null || !slugParam) return;
    setBusy(true);
    adminGuidelinesApi
      .saveMarkdown(slugParam, { markdown: draft, note, notify, recodify })
      .then((out) => {
        enqueueSnackbar(`Saved as v${out?.version}`, { variant: "success" });
        setDraft(null);
        setNote("");
        setNotify(false);
        setRecodify(false);
        setViewVersion(null);
        loadDoc(slugParam);
        fetchList();
      })
      .catch((err) =>
        enqueueSnackbar(errText(err, "Save failed"), { variant: "error" }),
      )
      .finally(() => setBusy(false));
  }, [draft, slugParam, note, notify, recodify, enqueueSnackbar, loadDoc, fetchList]);

  const handleSaveStructured = useCallback(
    (structured, structuredNote) => {
      if (!slugParam) return Promise.resolve();
      setBusy(true);
      return adminGuidelinesApi
        .saveStructured(slugParam, {
          structured,
          note: structuredNote || "rules edited",
        })
        .then((out) => {
          enqueueSnackbar(`Rules saved as v${out?.version}`, {
            variant: "success",
          });
          setViewVersion(null);
          loadDoc(slugParam);
          fetchList();
        })
        .catch((err) => {
          enqueueSnackbar(errText(err, "Couldn't save rules"), {
            variant: "error",
          });
          throw err;
        })
        .finally(() => setBusy(false));
    },
    [slugParam, enqueueSnackbar, loadDoc, fetchList],
  );

  const handleCodify = useCallback(() => {
    if (!slugParam) return;
    if (
      !window.confirm(
        "Re-run the codify agent on the current markdown? This creates a new version with freshly generated structured rules.",
      )
    )
      return;
    setBusy(true);
    adminGuidelinesApi
      .codify(slugParam)
      .then((out) => {
        enqueueSnackbar(
          `Codified as v${out?.version} — ${out?.ruleCount ?? 0} rules`,
          { variant: "success" },
        );
        setViewVersion(null);
        loadDoc(slugParam);
        fetchList();
      })
      .catch((err) =>
        enqueueSnackbar(errText(err, "Codify failed"), { variant: "error" }),
      )
      .finally(() => setBusy(false));
  }, [slugParam, enqueueSnackbar, loadDoc, fetchList]);

  const handleRevert = useCallback(
    (toVersion) => {
      if (!slugParam) return;
      if (
        !window.confirm(
          `Revert to v${toVersion}? Its artifacts are copied forward as a NEW current version — nothing is deleted.`,
        )
      )
        return;
      setBusy(true);
      adminGuidelinesApi
        .revert(slugParam, { toVersion, note: `reverted to v${toVersion}` })
        .then((out) => {
          enqueueSnackbar(`Reverted — now v${out?.version}`, {
            variant: "success",
          });
          setViewVersion(null);
          loadDoc(slugParam);
          fetchList();
        })
        .catch((err) =>
          enqueueSnackbar(errText(err, "Revert failed"), { variant: "error" }),
        )
        .finally(() => setBusy(false));
    },
    [slugParam, enqueueSnackbar, loadDoc, fetchList],
  );

  const versions = useMemo(() => doc?.versions || [], [doc]);

  const versionRows = useMemo(
    () =>
      versions
        .slice()
        .sort((a, b) => (b.v || 0) - (a.v || 0))
        .map((v) => ({
          at: v.createdAt,
          by: v.createdBy,
          action: v.action,
          toVersion: v.v,
          fromVersion: v.sourceVersion ?? null,
          note: v.note,
        })),
    [versions],
  );

  return (
    <AdminScreenShell
      icon={<DescriptionOutlined />}
      title="Guidelines"
      count={list?.length}
      loading={loadingList || loadingDoc || busy}
      subtitle="Versioned product guidelines: the markdown source, the codified rules the underwriting agents read, and the full edit history. Every save creates a new immutable version stamped with your user."
      actions={
        <>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={fetchList} disabled={loadingList}>
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<AutoFixHighOutlined />}
            onClick={handleCodify}
            disabled={!slugParam || busy}
          >
            Re-codify
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadFileOutlined />}
            onClick={() => setImportOpen(true)}
          >
            Import PDF
          </Button>
        </>
      }
    >
      <Flx g={2} sx={{ alignItems: "flex-start" }} wrap={false}>
        {/* Left rail. Single-column navigation list — UI chrome, not a data
            table, so it isn't an AG Grid. */}
        <Box sx={{ width: 260, flex: "0 0 260px" }}>
          <AdminCard title="Guidelines" dense>
            {list && list.length === 0 ? (
              <AdminEmpty>None imported yet.</AdminEmpty>
            ) : (
              <Box>
                {(list || []).map((g) => {
                  const active = g.slug === slugParam;
                  return (
                    <Box
                      key={g.slug}
                      onClick={() => navigate(`/admin/guidelines/${g.slug}`)}
                      sx={{
                        px: 2,
                        py: 1.25,
                        cursor: "pointer",
                        borderLeft: active
                          ? `3px solid ${teal[500]}`
                          : "3px solid transparent",
                        bgcolor: active ? "#f1f6f9" : "transparent",
                        "&:hover": { bgcolor: active ? "#f1f6f9" : "#f8fafc" },
                      }}
                    >
                      <Flx column g={0.25}>
                        <Txt
                          sx={{
                            fontSize: 13,
                            fontWeight: active ? 700 : 600,
                            color: "#0f172a",
                          }}
                        >
                          {g.title || g.slug}
                        </Txt>
                        <Txt sx={{ fontSize: 11, color: "#64748b" }}>
                          {[g.product, `v${g.currentVersion ?? "—"}`]
                            .filter(Boolean)
                            .join(" · ")}
                        </Txt>
                      </Flx>
                    </Box>
                  );
                })}
              </Box>
            )}
          </AdminCard>
        </Box>

        {/* Detail pane */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!slugParam ? (
            <AdminCard>
              <AdminEmpty>Select a guideline.</AdminEmpty>
            </AdminCard>
          ) : loadingDoc && !doc ? (
            <AdminCard>
              <Flx center sx={{ py: 6 }}>
                <CircularProgress size={22} />
              </Flx>
            </AdminCard>
          ) : !doc ? (
            <AdminCard>
              <AdminEmpty>Couldn't load this guideline.</AdminEmpty>
            </AdminCard>
          ) : (
            <>
              <AdminCard>
                <Flx ac jb g={1.5} wrap>
                  <Flx column g={0.5}>
                    <Flx ac g={1} wrap>
                      <Txt
                        sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}
                      >
                        {doc.title || doc.slug}
                      </Txt>
                      <Chip
                        size="small"
                        label={`v${doc.requestedVersion ?? doc.currentVersion}`}
                        sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                      />
                      {isHistoricalView ? (
                        <Chip
                          size="small"
                          label="viewing a prior version"
                          sx={{
                            height: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            bgcolor: "#fef3c7",
                            color: "#92400e",
                          }}
                        />
                      ) : null}
                    </Flx>
                    <Txt sx={{ fontSize: 11.5, color: "#64748b" }}>
                      {[
                        doc.product,
                        doc.version?.createdBy
                          ? `last edit by ${doc.version.createdBy}`
                          : null,
                        doc.version?.createdAt
                          ? formatDateTime(doc.version.createdAt)
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Txt>
                  </Flx>

                  <Flx ac g={1} wrap>
                    <TextField
                      select
                      size="small"
                      label="Version"
                      value={doc.requestedVersion ?? doc.currentVersion ?? ""}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setViewVersion(v);
                        loadDoc(slugParam, v);
                      }}
                      sx={{ width: 130 }}
                    >
                      {versions
                        .slice()
                        .sort((a, b) => (b.v || 0) - (a.v || 0))
                        .map((v) => (
                          <MenuItem key={v.v} value={v.v}>
                            v{v.v}
                            {v.v === doc.currentVersion ? " (current)" : ""}
                          </MenuItem>
                        ))}
                    </TextField>
                    <PillSwitcher items={VIEWS} value={view} onChange={setView} />
                  </Flx>
                </Flx>
              </AdminCard>

              {view === "document" ? (
                <AdminCard
                  title="Markdown source"
                  endContent={
                    draft == null ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setDraft(doc.markdown || "")}
                        disabled={isHistoricalView}
                      >
                        Edit
                      </Button>
                    ) : (
                      <Flx ac g={1}>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setDraft(null)}
                          disabled={busy}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleSaveMarkdown}
                          disabled={busy || draft === (doc.markdown || "")}
                        >
                          Save as v{(doc.currentVersion || 0) + 1}
                        </Button>
                      </Flx>
                    )
                  }
                >
                  {draft == null ? (
                    doc.markdown ? (
                      <GuidelineMarkdown markdown={doc.markdown} />
                    ) : (
                      <AdminEmpty>
                        This version has no markdown artifact.
                      </AdminEmpty>
                    )
                  ) : (
                    <Flx column g={1.5}>
                      <Flx g={1.5} sx={{ alignItems: "stretch" }} wrap={false}>
                        <TextField
                          multiline
                          minRows={22}
                          fullWidth
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          InputProps={{
                            sx: {
                              fontFamily:
                                "ui-monospace, SFMono-Regular, Menlo, monospace",
                              fontSize: 12.5,
                              alignItems: "flex-start",
                            },
                          }}
                          sx={{ flex: 1, minWidth: 0 }}
                        />
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            maxHeight: 560,
                            overflowY: "auto",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            p: 2,
                            bgcolor: "#fff",
                          }}
                        >
                          <GuidelineMarkdown markdown={draft} />
                        </Box>
                      </Flx>
                      <Flx ac g={2} wrap>
                        <TextField
                          size="small"
                          label="Change note"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          sx={{ minWidth: 280, flex: 1 }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={recodify}
                              onChange={(e) => setRecodify(e.target.checked)}
                            />
                          }
                          label={
                            <Txt sx={{ fontSize: 12.5 }}>
                              Re-run codify agent on save
                            </Txt>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={notify}
                              onChange={(e) => setNotify(e.target.checked)}
                            />
                          }
                          label={
                            <Txt sx={{ fontSize: 12.5 }}>
                              Notify admin / underwriting / processing
                            </Txt>
                          }
                        />
                      </Flx>
                    </Flx>
                  )}
                </AdminCard>
              ) : null}

              {view === "rules" ? (
                <GuidelineRulesPanel
                  structured={doc.structured}
                  readOnly={isHistoricalView || busy}
                  onSave={handleSaveStructured}
                />
              ) : null}

              {view === "versions" ? (
                <AdminCard title="Version history" dense>
                  <AdminEditLog
                    rows={versionRows}
                    onRevert={handleRevert}
                    empty="No versions recorded."
                  />
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Txt
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#0f172a",
                        display: "block",
                        mb: 1,
                        mt: 2,
                      }}
                    >
                      Edit log
                    </Txt>
                    <AdminEditLog rows={log} flush empty="No edits logged." />
                  </Box>
                </AdminCard>
              ) : null}

              {view === "transcript" ? (
                <AdminCard title="Codify agent transcript">
                  {doc.agentRaw ? (
                    <ExpandableBlock
                      collapsedMaxHeight={560}
                      label="Show full transcript"
                      surface="#0f172a"
                      color="#94a3b8"
                    >
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 2,
                          bgcolor: "#0f172a",
                          color: "#e2e8f0",
                          borderRadius: "8px",
                          fontSize: 11.5,
                          lineHeight: 1.6,
                        }}
                      >
                        {JSON.stringify(doc.agentRaw, null, 2)}
                      </Box>
                    </ExpandableBlock>
                  ) : (
                    <AdminEmpty>
                      No agent transcript stored for this version (only versions
                      produced by the codify agent carry one).
                    </AdminEmpty>
                  )}
                </AdminCard>
              ) : null}
            </>
          )}
        </Box>
      </Flx>

      <ImportGuidelinePdfDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(out) => {
          setImportOpen(false);
          fetchList();
          if (out?.slug) navigate(`/admin/guidelines/${out.slug}`);
        }}
      />
    </AdminScreenShell>
  );
}
